<template>
  <BModalConfirm
    ref="modal"
    class="LKPublicationsCardRecommendationAggregatedDialog"
    cancel-btn="Отменить"
    max-width="720px"
    disable-success-notification
    :action="generateAggregated"
    @closed="$emit('closed')"
  >
    <template #header>
      Генерация нескольких профилей школы
    </template>

    <div class="LKPublicationsCardRecommendationAggregatedModal">
      <div class="LKPublicationsCardRecommendationAggregatedModal__description">
        Выберите тип профиля и образовательные организации
      </div>

      <v-select
        v-model="profileType"
        :items="profileTypeItems"
        label="Тип профиля"
        item-text="text"
        item-value="value"
        outlined
        dense
        class="LKPublicationsCardRecommendationAggregatedModal__profile-type"
      />

      <component
        :is="ateTreeComponent"
        :regions.sync="regions"
        :municipalities.sync="municipalities"
        :schools.sync="schools"
        model=""
        multiple
        memoizeless
        min-height="400px"
        height="400px"
        max-height="400px"
      />
    </div>

    <template #footer="{ loading, close: closeModal }">
      <v-spacer />

      <v-btn
        text
        type="button"
        @click="closeModal()"
      >
        Отменить
      </v-btn>

      <v-btn
        depressed
        :loading="loading"
        :disabled="loading || !canGenerate"
        type="submit"
      >
        Запустить
      </v-btn>
    </template>
  </BModalConfirm>
</template>

<script>
import BModalConfirm from '@/components/modals/BModalConfirm.vue'
import {
  AteMunicipalitiesApi,
  AteSchoolsApi,
  LkRecomendationApi,
} from '@/api/resources'
import { rootMapper } from '@/store/modules/root'
import BAteTreeForm from '@/components/forms/BAteTree/BAteTreeForm.vue'
import BAteTreeFormFormdataStarted from '@/components/forms/BAteTree/BAteTreeFormFormdataStarted.vue'

const PAGE_SIZE = 10000

const PROFILE_TYPE_ITEMS = [
  { text: 'Административный профиль', value: 'adm' },
  { text: 'Региональный профиль', value: 'reg' },
  { text: 'Школьный профиль', value: 'sch' },
]

export default {
  name: 'LKPublicationsCardRecommendationAggregatedModal',
  components: {
    BModalConfirm,
    BAteTreeForm,
    BAteTreeFormFormdataStarted,
  },
  props: {
    publicationId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    regions: [],
    municipalities: [],
    schools: [],
    profileType: 'sch',
    profileTypeItems: PROFILE_TYPE_ITEMS,
  }),
  computed: {
    ...rootMapper.mapGetters(['profile']),
    ateTreeComponent() {
      return this.profile.is_superuser ? 'BAteTreeForm' : 'BAteTreeFormFormdataStarted'
    },
    hasSelectedAte() {
      return Boolean(
        this.regions.length
        || this.municipalities.length
        || this.schools.length,
      )
    },
    canGenerate() {
      return Boolean(this.profileType && this.hasSelectedAte)
    },
  },
  methods: {
    getAteList(response) {
      return response?.data || []
    },
    getMunicipalities(params) {
      const api = new AteMunicipalitiesApi()
      return this.profile.is_superuser
        ? api.getAll(params).then(this.getAteList)
        : api.getAllLK(params).then(this.getAteList)
    },
    getSchools(params) {
      const api = new AteSchoolsApi()
      return this.profile.is_superuser
        ? api.getAll(params).then(this.getAteList)
        : api.getAllLK(params).then(this.getAteList)
    },
    getAteCode(ate) {
      return ate?.code || ate?.id
    },
    getSchoolId(school, municipalityMap, regionCodeMap) {
      if (school.id) return school.id

      const municipality = school.municipality || municipalityMap.get(school.municipality_id)
      const regionCode = (
        this.getAteCode(school.region)
        || this.getAteCode(municipality?.region)
        || regionCodeMap.get(school.region_id || municipality?.region_id)
        || school.region_id
        || municipality?.region_id
      )
      const municipalityCode = (
        this.getAteCode(municipality)
        || this.getAteCode(school.municipality)
        || school.municipality_id
      )
      const schoolCode = school.code

      if (!regionCode || !municipalityCode || !schoolCode) {
        return null
      }

      return [regionCode, municipalityCode, schoolCode].join('|')
    },
    async getSchoolIds() {
      const municipalitiesFromRegions = await Promise.all(
        this.regions.map((region) => this.getMunicipalities({
          region_id: region.id,
          pageSize: PAGE_SIZE,
        })),
      )

      const municipalities = [
        ...this.municipalities,
        ...municipalitiesFromRegions.flat(),
      ]

      const schoolsFromMunicipalities = await Promise.all(
        municipalities.map((municipality) => this.getSchools({
          municipality_id: municipality.id,
          pageSize: PAGE_SIZE,
        })),
      )

      const regionCodeMap = new Map(
        this.regions.map((region) => [region.id, this.getAteCode(region)]),
      )
      const municipalityMap = new Map(
        municipalities.map((municipality) => [municipality.id, municipality]),
      )

      const schoolIds = [
        ...this.schools,
        ...schoolsFromMunicipalities.flat(),
      ].map((school) => this.getSchoolId(school, municipalityMap, regionCodeMap))
        .filter(Boolean)

      return [...new Set(schoolIds)]
    },
    open() {
      return this.$refs.modal.open()
    },
    close(flag) {
      return this.$refs.modal.close(flag)
    },
    async generateAggregated() {
      const schoolIds = await this.getSchoolIds()
      const api = new LkRecomendationApi()
      const result = await api.generateAggregated(
        this.publicationId,
        schoolIds,
        this.profileType,
        false,
      )

      this.$notice.info('Запущена генерация нескольких профилей школы')
      this.$emit('generated', result)
    },
  },
}
</script>

<style lang="scss">
.LKPublicationsCardRecommendationAggregatedModal {
  &__description {
    margin-bottom: 12px;
  }

  &__profile-type {
    margin-bottom: 12px !important;
  }

  .b-ate-tree-form {
    min-height: 400px;
    overflow-x: hidden;
  }

  .b-ate-tree-form__bottom {
    overflow-x: hidden;
  }
}

.LKPublicationsCardRecommendationAggregatedDialog {
  overflow-y: hidden;

  .v-card__text {
    overflow-y: hidden;
  }
}
</style>
